import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Check, Coffee, Store, Crown } from 'lucide-react';
import { usePlatform } from '@/hooks/use-platform';
import { Button } from '@/components/ui/button';
import { WaitlistModal } from './waitlist-modal';

/* ── plan configs (mirrored from pricing-section) ─── */
const PLANS = [
    {
        key: 'free',
        icon: Coffee,
        checkColor: 'text-[#6F4E37]',
        cardClass: 'bg-white/80 backdrop-blur-sm border border-[#C8A27A]/30 shadow-md',
        ctaVariant: 'outline' as const,
        ctaClass: 'border-[#C8A27A] text-[#6F4E37] hover:bg-[#6F4E37]/5',
        isPremium: false,
        isBusiness: false,
    },
    {
        key: 'premium',
        icon: Crown,
        checkColor: 'text-[#DAA520]',
        cardClass: 'bg-white border-2 border-[#DAA520] shadow-[0_0_30px_rgba(218,165,32,0.15)]',
        ctaVariant: 'default' as const,
        ctaClass: 'bg-[#6F4E37] hover:bg-[#5D3D2E] text-white hover:scale-[1.02] hover:shadow-lg',
        isPremium: true,
        isBusiness: false,
    },
    {
        key: 'business',
        icon: Store,
        checkColor: 'text-[#6F4E37]',
        cardClass: 'bg-white/80 backdrop-blur-sm border border-[#6B5344]/25 shadow-md',
        ctaVariant: 'outline' as const,
        ctaClass: 'border-[#6B5344]/30 text-[#6B5344] hover:bg-[#6B5344]/5',
        isPremium: false,
        isBusiness: true,
    },
] as const;

/* ═══════════════════════════════════════════════════════
   PricingMobileCarousel
   ═══════════════════════════════════════════════════════ */
export function PricingMobileCarousel() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { platform } = usePlatform();
    const [isWaitlistOpen, setIsWaitlistOpen] = useState(false);
    const [emblaRef, emblaApi] = useEmblaCarousel({
        loop: false,
        startIndex: 1, // Premium card first
        align: 'center',
    });
    const [selectedIndex, setSelectedIndex] = useState(1);
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

    const scrollTo = useCallback(
        (index: number) => emblaApi?.scrollTo(index),
        [emblaApi]
    );

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        setSelectedIndex(emblaApi.selectedScrollSnap());
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        onSelect();
        setScrollSnaps(emblaApi.scrollSnapList());
        emblaApi.on('select', onSelect);

        // Swipe hint: subtle bounce on first load
        const timer = setTimeout(() => {
            const currentScroll = emblaApi.selectedScrollSnap();
            emblaApi.scrollTo(currentScroll); // re-settle to trigger internal animation
        }, 600);

        return () => {
            emblaApi.off('select', onSelect);
            clearTimeout(timer);
        };
    }, [emblaApi, onSelect]);

    return (
        <div className="xl:hidden w-full relative">
            {/* Carousel viewport */}
            <div className="overflow-hidden pt-6" ref={emblaRef}>
                <div className="flex touch-pan-y -ml-4 items-stretch">
                    {PLANS.map((plan, index) => {
                        const Icon = plan.icon;
                        const isActive = selectedIndex === index;

                        return (
                            <div
                                className="flex-[0_0_85%] min-w-0 pl-4 relative h-auto"
                                key={plan.key}
                            >
                                <div
                                    className={cn(
                                        'relative flex flex-col rounded-[32px] p-7 pt-10 overflow-visible transition-all duration-300 h-full',
                                        plan.cardClass,
                                        plan.isPremium && 'animate-[glow-pulse_3s_ease-in-out_infinite]',
                                        isActive
                                            ? 'opacity-100 scale-100'
                                            : 'opacity-60 scale-[0.92]'
                                    )}
                                >
                                    {/* Badge (Premium & Business) */}
                                    {(plan.isPremium || plan.isBusiness) && (
                                        <div className="absolute -top-4 left-0 right-0 flex justify-center z-50">
                                            <span
                                                className={cn(
                                                    'text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-widest',
                                                    plan.isPremium
                                                        ? 'bg-gradient-to-r from-[#DAA520] to-[#B87333] text-white shadow-lg shadow-amber-500/25'
                                                        : 'bg-[#EDE5DC] text-[#6B5344] border border-[#6B5344]/20'
                                                )}
                                            >
                                                {t(`pricing.plans.${plan.key}.badge`)}
                                            </span>
                                        </div>
                                    )}

                                    {/* Card Header */}
                                    <div className="mb-5">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div
                                                className={cn(
                                                    'w-10 h-10 rounded-xl flex items-center justify-center',
                                                    plan.isPremium
                                                        ? 'bg-gradient-to-br from-[#DAA520] to-[#B87333] text-white'
                                                        : 'bg-[#6F4E37]/10 text-[#6F4E37]'
                                                )}
                                            >
                                                <Icon className="w-5 h-5" />
                                            </div>
                                            <h3 className="text-2xl font-bold text-[#2D1810]">
                                                {t(`pricing.plans.${plan.key}.name`)}
                                            </h3>
                                        </div>
                                        <p className="text-sm text-[#6B5344]">
                                            {t(`pricing.plans.${plan.key}.description`)}
                                        </p>
                                    </div>

                                    {/* Price Block */}
                                    <div className="mb-5">
                                        {plan.isPremium ? (
                                            <div className="space-y-3">
                                                <div>
                                                    <span className="text-4xl font-bold text-[#2D1810]">
                                                        {t('pricing.plans.premium.heroPrice')}
                                                    </span>
                                                    <p className="text-sm text-[#9C8979] font-medium mt-1">
                                                        {t('pricing.plans.premium.heroSubtext')}
                                                    </p>
                                                </div>
                                                <div className="bg-[#F5EDE4] rounded-xl p-3 space-y-1">
                                                    <p className="text-xs font-semibold text-[#6B5344] uppercase tracking-wide">
                                                        {t('pricing.plans.premium.tiers.label')}
                                                    </p>
                                                    {(
                                                        t('pricing.plans.premium.tiers.items', {
                                                            returnObjects: true,
                                                        }) as string[]
                                                    )?.map((tier, i) => (
                                                        <p key={i} className="text-xs text-[#9C8979]">
                                                            {tier}
                                                        </p>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                <span className="text-4xl font-bold text-[#2D1810]">
                                                    {t(`pricing.plans.${plan.key}.price`)}
                                                </span>
                                                <span className="text-[#9C8979] ml-1">
                                                    {t(`pricing.plans.${plan.key}.period`)}
                                                </span>
                                                {plan.isBusiness && (
                                                    <p className="text-sm text-[#6B5344] mt-1">
                                                        {t('pricing.plans.business.subtitle')}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Features List */}
                                    <ul className="space-y-3 text-sm flex-1 mb-6">
                                        {(
                                            t(`pricing.plans.${plan.key}.features`, {
                                                returnObjects: true,
                                            }) as string[]
                                        )?.map((feature, i) => (
                                            <li key={i} className="flex items-start gap-2.5">
                                                <Check
                                                    className={`h-4 w-4 mt-0.5 shrink-0 ${plan.checkColor}`}
                                                />
                                                <span className="text-[#6B5344]">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    {/* CTA */}
                                    <Button
                                        className={cn(
                                            'w-full rounded-full py-6 text-base font-semibold transition-all duration-300',
                                            plan.ctaClass
                                        )}
                                        variant={plan.ctaVariant}
                                        onClick={() => {
                                            if (plan.key === 'free') {
                                                if (platform === 'ios') {
                                                    window.open('https://apps.apple.com', '_blank');
                                                } else {
                                                    window.open('https://play.google.com', '_blank');
                                                }
                                            } else if (plan.key === 'premium') {
                                                navigate('/register?plan=premium');
                                            } else if (plan.key === 'business') {
                                                setIsWaitlistOpen(true);
                                            }
                                        }}
                                    >
                                        {t(`pricing.plans.${plan.key}.cta`)}
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Pagination Dots */}
            <div className="flex justify-center gap-2 mt-6">
                {scrollSnaps.map((_, index) => (
                    <button
                        key={index}
                        className={cn(
                            'w-2 h-2 rounded-full transition-all duration-300',
                            index === selectedIndex
                                ? 'bg-[#6F4E37] w-6'
                                : 'bg-[#6F4E37]/30'
                        )}
                        onClick={() => scrollTo(index)}
                        aria-label={`Go to plan ${index + 1}`}
                    />
                ))}
            </div>

            {/* Waitlist Modal */}
            <WaitlistModal open={isWaitlistOpen} onOpenChange={setIsWaitlistOpen} />
        </div>
    );
}
